/**
 * ButtonGrid — a rows × cols grid of cells with configurable selection
 * semantics driven by `item.selectionMode`:
 *
 *   'radio' (default) — exactly one active or none. Tap different =
 *     off old + on new. Tap active = off.
 *   'radio-required' — always exactly one active. Tap different = swap.
 *     Tap active = no-op.
 *   'multi' — every cell is an independent toggle. No cross-cell writes.
 *   'up-to-n' — up to `maxActive` cells active at once. When at cap, a
 *     new tap evicts the least-recently-activated cell first.
 *
 * Cell lit state flips only once OPBridge confirms the send (DEC-124
 * Clause B, 2026-07-31) — never optimistically on tap. A failed send
 * leaves a cell exactly where it was. (2026-08-20: the legacy bridge's
 * `midi.in` corrective echo this doc used to describe is removed —
 * `packages/bridge/` is deleted, and the echo had been unreachable for
 * months regardless; dispatch confirmation was always the real mechanism.)
 * Cells are also `disabled` whenever no bridge is connected (Clause A),
 * matching FireButton.
 *
 * Per-cell edit affordance: in edit mode, the grid's Inspector exposes
 * an "Edit cells" toggle. When ON, the Inspector renders a list of
 * per-cell rows (label / CC / channel / color) that the user edits
 * inline; clicking a cell in the grid scrolls its row into view and
 * focuses its Label input. Bulk-labeling a 16-cell grid means: EDIT
 * → select grid → Edit cells ON → tab / click through rows. Toggle
 * OFF (or press Esc) to leave edit-cells mode.
 *
 * Superseded designs: portalled per-cell popup (2026-07-07 morning) —
 * hit-testing and positioning drift made it unreliable. Long-press
 * (before that) — slow, undiscoverable, finicky on trackpads.
 */

import { createMemo, createSignal, createEffect, onCleanup, onMount, For, Index, Show, type Accessor } from 'solid-js';
import type { MidiCcStep } from '@opbuttons/schema';
import { MatrixPicker } from './MatrixPicker.tsx';
import { fireAction, fanoutTargets, type FireResult } from '../opbridge/dispatch.ts';
import { ulid } from '../utils/ulid.ts';
import { splitLabelLines, fitPadName } from './autoShrinkLabel.ts';
import { anyBridgeConnectedReactive } from '../opbridge/store.ts';
import type { ButtonGridItem, ButtonGridCell, CellColor } from '../layout/store.ts';
import { editGridCell, selectItem, selectedItemId } from '../layout/store.ts';
import { isEditMode } from '../layout/store.ts';
import { hueOf } from '../layout/projectStore.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { InstrumentIcon } from './InstrumentIcon.tsx';
import {
  LOOK_DEFAULTS, textureFor, texturePx, STARTS_BEATS, nextLine, flashBrightness,
} from '../layout/buttonLook.ts';
import { HUE_VALUES } from '../layout/projectStore.ts';
import { reportSend, controlName } from '../ui/pressReport.ts';

/** Fire a CC value through the OPBridge dispatcher — the same path
 *  FireButton uses — and resolve with the dispatch result so the caller can
 *  gate the lit face on it (DEC-124 Clause B). `channel0` is 0..15; the
 *  dispatcher expects 1..16 on the step, so we +1 at the boundary. */
async function mirrorToFireAction(
  cc: number, value: number, channel0: number, bridgeIds: string[] | undefined,
): Promise<FireResult[]> {
  const results = await Promise.all(fanoutTargets(bridgeIds).map((target) => {
    const step: MidiCcStep = {
      type: 'midi.cc',
      id: ulid(),
      target,
      channel: channel0 + 1,
      cc,
      value,
    };
    return fireAction(step);
  }));
  return results.flat();
}

interface ButtonGridProps {
  item: ButtonGridItem;
  /** The song beat (smoothClock), for Starts' waiting ring and the beat flash (DEC-317). Absent = no clock: a press
   *  lights as soon as it is confirmed and nothing flashes. */
  beat?: Accessor<number>;
  bpm?: Accessor<number>;
  /** Whether the clock is running. Stopped, nothing waits and nothing flashes. */
  playing?: Accessor<boolean>;
}

/** A press that is showing the receiver's wait (DEC-317's Starts). `held` is what the grid SHOWED when the first press
 *  of the wait landed; the grid keeps showing it until the line, then shows the confirmed state. Visual only: every
 *  press was sent and planned against the confirmed state exactly as before. */
interface Wait { cellId: string; from: number; at: number; held: Record<string, boolean> }

const CELL_COLOR_TOKEN: Record<CellColor, string> = {
  cyan:    'idle',
  magenta: 'incoming',
  amber:   'armed',
  green:   'ok',
  violet:  'violet',
  yellow:  'yellow',
  pink:    'pink',
  sky:     'sky',
  tan:     'tan',
  red:     'error',
};

// Module-scope signal for "cells are click-to-edit" mode. Set to the
// grid item id whose Inspector toggle is currently ON, or null. Hoisted
// out of the component so it survives ButtonGrid remounts (App.tsx's
// <For> keys by identity and any editGridCell call swaps the item
// reference, remounting the row).
const [editingGridId, setEditingGridId] = createSignal<string | null>(null);

/** Each grid's lit cells, by grid item id, outliving the component for the
 *  same reason as `editingGridId` above: a page switch, an Inspector edit or a
 *  sync merge remounts the grid. Lit state lived only in the mount, so after
 *  any of those every cell read OFF while Live still held one ON, and a radio
 *  grid's next tap sent ON for the new cell with no OFF for the old one: two
 *  "exclusive" cells on in Live (2026-11-03 audit A-6). */
const gridLitMemory = new Map<string, Record<string, boolean>>();
// Signal + monotonic seq for "focus this cell's row in the Inspector."
// The Inspector's cell-list row registers an effect that reads this
// signal and, when its cellId matches, scrolls itself into view + focuses
// its Label input. Seq is bumped on every click so the same cell can be
// re-focused (identity-wise the signal wouldn't change otherwise).
const [focusCellRequest, setFocusCellRequest] = createSignal<{ cellId: string; seq: number } | null>(null);
let focusCellSeq = 0;
export function isEditingCellsFor(gridId: string): boolean {
  return editingGridId() === gridId;
}
export function toggleEditingCellsFor(gridId: string): void {
  setEditingGridId(editingGridId() === gridId ? null : gridId);
}
export function stopEditingCells(): void {
  setEditingGridId(null);
}
export function focusCellRow(): Accessor<{ cellId: string; seq: number } | null> {
  return focusCellRequest;
}
export function requestFocusCell(cellId: string): void {
  focusCellSeq += 1;
  setFocusCellRequest({ cellId, seq: focusCellSeq });
}

// Module-scope memory of the .bg-cell-rows scroll position, keyed by
// grid id. Committing a label edit spreads the ButtonGridItem — that
// triggers a Solid <Show> remount higher up in the InspectorPanel,
// which throws away the DOM including our scroll container. Without
// this, the operator gets snapped back to row 1 after every label
// commit. Read on mount + write on scroll to preserve position across
// remounts.
const rowsScrollByGrid = new Map<string, number>();

/** Resolve the effective CC / channel / onValue for a cell, falling back
 *  to the grid's defaults when a per-cell override is absent. */
function resolveCC(grid: ButtonGridItem, cell: ButtonGridCell, index: number): number {
  return cell.ccNumber ?? ((grid.baseCC + index) & 0x7f);
}
function resolveChannel(grid: ButtonGridItem, cell: ButtonGridCell): number {
  return cell.channel ?? grid.channel;
}
function resolveOnValue(grid: ButtonGridItem, cell: ButtonGridCell): number {
  return cell.onValue ?? grid.onValue;
}

export function ButtonGrid(props: ButtonGridProps) {
  // Show-mode guard (SD-5): first tap on a cell arms it (2s), second fires.
  // Per-cell arm keyed by cell id.
  const guard = createDiscreteGuard(() => !!props.item.guard);

  // True when at least one of the grid's configured bridges is connected —
  // drives the disabled state + the early-return guard in onCellPointerUp
  // (DEC-124 Clause A). Multi-select bridge affinity, 2026-08-13.
  const anyTransportLive = () => anyBridgeConnectedReactive(props.item.bridgeIds);

  // Per-cell lit state (DEC-124 Clause B): flips only once OPBridge
  // confirms the send with `kind: 'ok'`; a failed send leaves a cell
  // exactly where it was. Nothing echoes it back today (no inbound
  // subscription), so it is remembered across remounts (`gridLitMemory`).
  // cellId → boolean; absent = off.
  const [lit, setLitSig] = createSignal<Record<string, boolean>>(gridLitMemory.get(props.item.id) ?? {});
  const setLit = (next: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)): void => {
    const v = typeof next === 'function' ? next(lit()) : next;
    setLitSig(v);
    gridLitMemory.set(props.item.id, v);
  };
  // Pending state: cell id that was just tapped, before the dispatch
  // resolves or an echo arrives. Cleared automatically by a 250ms timer OR
  // by the next echo.
  const [pending, setPending] = createSignal<string | null>(null);
  let pendingTimer: number | null = null;
  // LRU history of activation order — head is oldest, tail is newest.
  // Used by 'up-to-n' mode to evict the oldest active cell when at cap.
  // Only ids currently lit=true appear; cells falling off drop from
  // history in `fireCellChange`.
  let activationHistory: string[] = [];

  const mode = (): 'radio' | 'radio-required' | 'multi' | 'up-to-n' =>
    props.item.selectionMode ?? 'radio';

  // ---- The button look (DEC-316 / DEC-317) ----
  const [waiting, setWaiting] = createSignal<Wait | null>(null);
  /** Beats left on the waiting pad's count (4 3 2 1). A signal: it changes a few times a bar, not every frame. */
  const [waitCount, setWaitCount] = createSignal(0);
  /** What a cell SHOWS: the held state while a wait runs, else the confirmed state. */
  const shownOn = (cellId: string): boolean => {
    const w = waiting();
    return w ? w.held[cellId] === true : lit()[cellId] === true;
  };
  const titleText = () => (props.item.name ?? '').trim();
  const titleAt = () => (titleText() ? props.item.titleAt ?? LOOK_DEFAULTS.titleAt : 'off');
  const icon = () => (props.item.icon && props.item.icon !== 'none' ? props.item.icon : undefined);
  const iconAt = () => (icon() ? props.item.iconAt ?? LOOK_DEFAULTS.iconAt : 'off');
  const texture = () => textureFor(icon() ?? 'none', props.item.texture, props.item.texSize);
  const flashStyle = () => ((props.item.starts ?? 'now') === 'now' ? 'off' : props.item.beatFlash ?? LOOK_DEFAULTS.beatFlash);
  const gapPx = () => props.item.gap ?? 5;
  /** The pad's shorter side in layout px, from the item's size: the tray's 4 px each side, the title band's 10 px
   *  (14 up the side), and the gaps. Drives the texture's scale and the corner icon's cap. */
  const padSide = () => {
    const t = titleAt();
    const padX = 8 + (t === 'side' ? 14 : 0);
    const padY = 8 + (/^[tb]/.test(t) ? 10 : 0);
    const g = gapPx();
    return Math.max(8, Math.min(
      (props.item.w - padX - (props.item.cols - 1) * g) / props.item.cols,
      (props.item.h - padY - (props.item.rows - 1) * g) / props.item.rows,
    ));
  };
  const texPx = () => { const t = texture(); return t.pattern === 'smooth' ? 0 : texturePx(padSide(), t.pattern, t.size); };
  const texColour = () => props.item.texColour ?? 'auto';
  let gridEl: HTMLDivElement | undefined;

  // The clock, read only while something needs it (a wait, or a flashing lit pad), so an idle grid does no per-frame
  // work: Solid tracks the beat only on the branch that reads it.
  createEffect(() => {
    const w = waiting();
    const running = props.playing?.() ?? false;
    const style = flashStyle();
    const needFlash = style !== 'off' && running && !!props.beat && props.item.cells.some((c) => shownOn(c.id));
    if (!w && !needFlash) { gridEl?.style.removeProperty('--bp'); return; }
    const b = props.beat ? props.beat() : 0;
    if (w) {
      if (!running || !props.beat || b >= w.at) {
        setWaiting(null);
      } else {
        gridEl?.style.setProperty('--qp', Math.min(1, (b - w.from) / Math.max(0.001, w.at - w.from)).toFixed(3));
        setWaitCount(Math.max(1, Math.ceil(w.at - b - 1e-9)));
      }
    }
    if (needFlash) gridEl?.style.setProperty('--bp', flashBrightness(style, b, props.bpm?.() ?? 120).toFixed(3));
  });

  /** Press feel (DEC-317): the pad sinks under the finger and light spreads from where it was touched. */
  function pressFeel(el: HTMLElement, e: PointerEvent): void {
    el.dataset.press = 'true';
    const r = el.getBoundingClientRect();
    const rc = el.querySelector('.bg-cell-rc');
    if (!rc || r.width === 0) return;
    const rp = document.createElement('i');
    rp.className = 'bg-cell-rip';
    rp.style.left = `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`;
    rp.style.top = `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`;
    rc.appendChild(rp);
    window.setTimeout(() => rp.remove(), 700);
  }
  const unpress = (el: HTMLElement) => { delete el.dataset.press; };

  // Subscribe to inbound MIDI. For each cell whose (cc, channel) matches
  // the incoming message, update its lit state. Echo always wins over
  // the optimistic flip — Ableton is authoritative when it disagrees.
  /** All cell ids currently lit. */
  const activeCellIds = createMemo<string[]>(() => {
    const map = lit();
    return props.item.cells.filter((c) => map[c.id]).map((c) => c.id);
  });

  /** Fire one cell's confirmed ON/OFF write and commit it into `lit` + the
   *  LRU history — but ONLY once OPBridge confirms (DEC-124 Clause B). A
   *  cell that 422s is left exactly where it was, never flipped
   *  speculatively in either direction — the same rule FireButton's
   *  toggle mode follows. */
  async function fireCellChange(cell: ButtonGridCell, index: number, turningOn: boolean): Promise<void> {
    const cc = resolveCC(props.item, cell, index);
    const ch = resolveChannel(props.item, cell);
    const value = turningOn ? resolveOnValue(props.item, cell) : props.item.offValue;
    const results = await mirrorToFireAction(cc, value, ch, props.item.bridgeIds);
    if (!results.every((r) => r.kind === 'ok')) { reportSend(props.item.id, controlName(props.item, 'Grid'), results); return; }
    setLit((prev) => ({ ...prev, [cell.id]: turningOn }));
    activationHistory = activationHistory.filter((id) => id !== cell.id);
    if (turningOn) activationHistory.push(cell.id);
  }

  function fireCell(cell: ButtonGridCell, index: number): void {
    // PLANNING phase — decides which cells change and to what, against a
    // local draft + a local copy of the LRU history. Identical selection
    // logic to before the DEC-124 rewrite; the only change is that this
    // phase no longer fires anything or touches real state — it just
    // decides the plan, which the dispatch phase below fires and commits
    // cell-by-cell, independently, once each is confirmed.
    const draft: Record<string, boolean> = { ...lit() };
    const isActive = draft[cell.id] === true;
    const m = mode();
    let plannedHistory = activationHistory.slice();

    function planOff(cellId: string): void {
      draft[cellId] = false;
      plannedHistory = plannedHistory.filter((id) => id !== cellId);
    }
    function planOn(cellId: string): void {
      draft[cellId] = true;
      plannedHistory = plannedHistory.filter((id) => id !== cellId);
      plannedHistory.push(cellId);
    }

    if (m === 'multi') {
      // Every cell is its own toggle. No cross-cell writes.
      if (isActive) planOff(cell.id);
      else planOn(cell.id);
    } else if (m === 'radio') {
      // One active or none. Tap active = all-off. Tap different = swap.
      if (isActive) {
        planOff(cell.id);
      } else {
        for (const id of activeCellIds()) planOff(id);
        planOn(cell.id);
      }
    } else if (m === 'radio-required') {
      // Always exactly one. Tap active = no-op. Tap different = swap.
      if (isActive) return; // no-op — can't turn off the required one
      for (const id of activeCellIds()) planOff(id);
      planOn(cell.id);
    } else if (m === 'up-to-n') {
      // Cap at maxActive. Tap active = deactivate; tap inactive =
      // activate, evicting the oldest active cell if at cap.
      if (isActive) {
        planOff(cell.id);
      } else {
        const cap = Math.max(1, props.item.maxActive ?? 2);
        const activeInDraft = Object.keys(draft).filter((id) => draft[id]);
        while (activeInDraft.length >= cap) {
          const oldest = plannedHistory.find((id) => draft[id]);
          if (!oldest) break; // safety: history desynced somehow
          planOff(oldest);
          const ix = activeInDraft.indexOf(oldest);
          if (ix !== -1) activeInDraft.splice(ix, 1);
        }
        planOn(cell.id);
      }
    }

    // Tactile-feedback pending pulse on newly-activated taps (not
    // deactivations — the visual change is already dramatic enough). Fires
    // immediately regardless of send outcome — it means "you tapped this",
    // not "it landed".
    if (!isActive) {
      setPending(cell.id);
      if (pendingTimer !== null) clearTimeout(pendingTimer);
      pendingTimer = window.setTimeout(() => {
        setPending(null);
        pendingTimer = null;
      }, 250);
    } else {
      setPending(null);
    }

    // STARTS (DEC-317): when the receiver waits for a line, the pad SHOWS that wait. Only the display is held; the
    // dispatch below is unchanged. A second press on the waiting pad keeps its wait (that press was sent too); a press
    // on another pad moves the wait to it, still showing what was shown before the first.
    const len = STARTS_BEATS[props.item.starts ?? 'now'];
    if (len > 0 && (props.playing?.() ?? false) && props.beat) {
      const b = props.beat();
      const w = waiting();
      if (!w || w.cellId !== cell.id) {
        setWaiting({ cellId: cell.id, from: b, at: nextLine(b, len), held: w ? w.held : { ...lit() } });
      }
    }

    // DISPATCH phase — fire every cell the plan actually changed, each
    // independently gated on its own confirmation.
    const before = lit();
    for (const cellId of Object.keys(draft)) {
      const turningOn = draft[cellId] === true;
      if (before[cellId] === turningOn) continue; // untouched by this tap
      const ix = props.item.cells.findIndex((c) => c.id === cellId);
      if (ix === -1) continue;
      void fireCellChange(props.item.cells[ix]!, ix, turningOn);
    }
  }

  // — long-press to open cell editor ——————————————————————————

  const isEditingCells = () => editingGridId() === props.item.id;

  function onCellPointerDown(_cell: ButtonGridCell, _index: number, e: PointerEvent): void {
    if (!isEditMode()) {
      // Test mode: only the press feel; the press itself fires on pointerup as before.
      if (anyTransportLive() && selectedItemId() !== props.item.id) pressFeel(e.currentTarget as HTMLElement, e);
      return;
    }
    // In edit-cells mode, the cell owns the whole gesture (tap = focus
    // its Inspector row). Stop the wrapper's click-vs-drag arbiter from
    // taking over — its setPointerCapture would move the pointer away
    // from the cell and the cell's pointerup wouldn't fire, so the
    // focus request never happens.
    //
    // In plain edit mode, we WANT the wrapper's arbiter to run — that's
    // what enables "single-click cell to open grid Inspector, click+drag
    // cell to move the whole grid." So we DON'T stopPropagation there.
    if (isEditingCells()) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  function onCellPointerUp(cell: ButtonGridCell, index: number, _e: PointerEvent): void {
    if (isEditMode()) {
      if (isEditingCells()) {
        // Edit-cells mode: ask the Inspector to scroll this cell's row
        // into view + focus its Label input. Row rendering is the
        // Inspector's business — this component just signals which one.
        requestFocusCell(cell.id);
      }
      // In plain edit mode we do nothing on pointerup — the wrapper's
      // click-vs-drag arbiter handles both select-on-click and
      // move-on-drag. If we called selectItem here too, a small
      // finger-jitter drag would fire selectItem right before/after the
      // wrapper's own moveItem, causing the Inspector to open even
      // though the user intended to drag.
      return;
    }
    // Right-click escape hatch: when the grid is selected in test mode,
    // the wrapper owns the gesture (drag to move). Don't fire MIDI.
    if (selectedItemId() === props.item.id) return;
    if (!anyTransportLive()) return; // DEC-124 Clause A — dead when there's no bridge
    if (!guard.gate(cell.id)) return; // guarded: first tap only armed this cell
    fireCell(cell, index);
  }

  function onCellPointerCancel(): void {
    // No long-press timer to cancel anymore.
  }

  // — render —————————————————————————————————————————————————

  return (
    <div
      class="bg-grid"
      ref={(el) => (gridEl = el)}
      style={{
        '--bg-cols': props.item.cols,
        '--bg-rows': props.item.rows,
        gap: `${gapPx()}px`,
        width: `${props.item.w}px`,
        height: `${props.item.h}px`,
        ...(icon() ? { '--icon-px': `${Math.round(Math.min(15, padSide() * 0.24))}px` } : {}),
      }}
      data-disabled={anyTransportLive() ? 'false' : 'true'}
      data-editing-cells={isEditingCells() ? 'true' : 'false'}
      data-finish={props.item.finish ?? LOOK_DEFAULTS.finish}
      data-title={titleAt()}
      data-icon-at={iconAt()}
      data-narrow={titleText() && props.item.w < titleText().length * 8 + 40 ? 'true' : undefined}
    >
      {/* The grid's Name IN LINE with it (DEC-316): a band inside the tray, so grids sit edge to edge. */}
      <Show when={titleAt() !== 'off'}>
        <span class="bg-title">
          <Show when={iconAt() === 'title' && icon()}>
            <InstrumentIcon id={icon()!} />
          </Show>
          <span class="bg-title-text">{titleText()}</span>
        </span>
      </Show>
      <For each={props.item.cells}>
        {(cell, index) => {
          const isActive = createMemo(() => shownOn(cell.id));
          const isWaiting = createMemo(() => waiting()?.cellId === cell.id);
          const isPending = createMemo(() => pending() === cell.id);
          // A cell with no colour of its own takes the grid's (the item's
          // --hue, set on the wrapper); with neither, the old cyan.
          const colorToken = (): string | undefined =>
            cell.color ? CELL_COLOR_TOKEN[cell.color]
              : hueOf(props.item) === 'none' ? CELL_COLOR_TOKEN.cyan : undefined;
          const effectiveCC = createMemo(() => resolveCC(props.item, cell, index()));
          const labelText = () => cell.label ?? String(index() + 1);
          const nameSize = () => cell.nameSize ?? props.item.nameSize ?? 'auto';
          // What the name shows: its own lines, or two lines when that makes a two-word name bigger (fitPadName).
          const [shownLines, setShownLines] = createSignal<string[]>(splitLabelLines(labelText()));

          // Names fitted to the pad (DEC-316 / DEC-317): Auto is as big as the pad allows and GROWS as well as shrinks;
          // a fixed size is kept unless the name would run off the pad. Never under 9 px. Re-fitted on resize.
          let cellEl: HTMLButtonElement | undefined;
          let labelEl: HTMLSpanElement | undefined;
          let ro: ResizeObserver | undefined;
          // Deferred re-fits (a frame, the fonts) can land after this cell was replaced: never read its props then.
          let disposed = false;
          onCleanup(() => { disposed = true; });
          const refit = () => {
            if (disposed || !cellEl || !labelEl) return;
            const lines = fitPadName(cellEl, labelEl, labelText(), nameSize() === 'auto', iconAt() === 'above');
            if (lines) setShownLines(lines);
          };
          createEffect(() => {
            void labelText(); void nameSize(); void iconAt(); void isEditMode(); void props.item.hideLabel;
            requestAnimationFrame(refit);
          });
          onMount(() => {
            if (!cellEl) return;
            ro = new ResizeObserver(() => refit());
            ro.observe(cellEl);
            // A web font that lands after the first fit changes every width without resizing anything, so the
            // ResizeObserver never fires: re-fit once the fonts are in (a two-word name stayed on one line).
            void document.fonts?.ready.then(() => requestAnimationFrame(refit));
          });
          onCleanup(() => { if (ro) ro.disconnect(); });

          return (
            <button
              class="bg-cell"
              type="button"
              ref={(el) => (cellEl = el)}
              data-color={colorToken()}
              data-active={isActive() ? 'true' : 'false'}
              data-armed={guard.armedKey() === cell.id ? 'true' : 'false'}
              data-pending={isPending() ? 'true' : 'false'}
              data-waiting={isWaiting() ? 'true' : undefined}
              onPointerDown={(e) => onCellPointerDown(cell, index(), e)}
              onPointerUp={(e) => { unpress(e.currentTarget); onCellPointerUp(cell, index(), e); }}
              onPointerCancel={(e) => { unpress(e.currentTarget); onCellPointerCancel(); }}
              onPointerLeave={(e) => { unpress(e.currentTarget); onCellPointerCancel(); }}
              title={`CC ${effectiveCC()} \u00b7 ch ${resolveChannel(props.item, cell) + 1}`}
              // Test mode only (DEC-124 Clause A is about dispatch tappability,
              // not authoring): edit mode's per-cell "Edit cells" flow - click
              // to focus a cell's Inspector row, drag to move the grid - needs
              // every cell interactive regardless of bridge state, the same
              // way an operator can lay out a grid with no bridge paired yet.
              disabled={!isEditMode() && !anyTransportLive()}
            >
              {/* Texture (DEC-317): two layers cut by the pattern, a groove a hair below and a lit edge a hair above,
                  faded out under the name so the legend sits on a clean field. */}
              <Show when={texPx() > 0}>
                <span
                  class="bg-cell-tx"
                  data-tx={texture().pattern}
                  data-tc={texColour() === 'auto' ? undefined : texColour() === 'dark' ? 'dark' : 'hue'}
                  style={{
                    '--txs': `${texPx()}px`,
                    '--txo': ((props.item.texStrength ?? LOOK_DEFAULTS.texStrength) / 100).toFixed(2),
                    ...(texColour() !== 'auto' && texColour() !== 'dark'
                      ? { '--tc': HUE_VALUES[texColour() as keyof typeof HUE_VALUES] } : {}),
                  }}
                >
                  <i class="s" /><i class="h" />
                </span>
              </Show>
              <Show when={iconAt() === 'above' && icon()}>
                <InstrumentIcon id={icon()!} class="bg-cell-icon" />
              </Show>
              <span
                class="bg-cell-label"
                data-name-size={nameSize()}
                ref={(el) => (labelEl = el)}
              >
                <For each={shownLines()}>
                  {(line) => <span class="face-line">{line}</span>}
                </For>
              </span>
              <Show when={iconAt() === 'corner' && icon()}>
                <InstrumentIcon id={icon()!} class="bg-cell-icon bg-cell-icon-corner" />
              </Show>
              {!props.item.hideLabel && !isEditMode() && (
                <span class="bg-cell-index">{index() + 1}</span>
              )}
              {isEditMode() && (
                <>
                  <span class="bg-cell-info">cc <b>{effectiveCC()}</b></span>
                  <span class="bg-cell-info">ch <b>{resolveChannel(props.item, cell) + 1}</b></span>
                </>
              )}
              {/* Press ripple, and the waiting ring with the beats left (Starts, DEC-317). */}
              <span class="bg-cell-rc" aria-hidden="true" />
              <Show when={isWaiting()}>
                <span class="bg-cell-q" aria-hidden="true" />
                <span class="bg-cell-qn" aria-hidden="true">{waitCount()}</span>
              </Show>
            </button>
          );
        }}
      </For>
    </div>
  );
}

// ====================================================================
// Per-cell inline editor rows — rendered by the Inspector when
// edit-cells mode is on. One row per cell: Label / CC / Ch / Color.
// Kept in this file so ALL_COLORS + resolvers stay co-located with
// the render code above.
// ====================================================================

const ALL_COLORS: CellColor[] = [
  'cyan', 'magenta', 'amber', 'green',
  'violet', 'yellow', 'pink', 'sky', 'tan', 'red',
];

const NAME_SIZES: { id: 'auto' | 'S' | 'M' | 'L' | 'XL'; label: string; title: string }[] = [
  { id: 'auto', label: 'Auto', title: 'Per-line auto-shrink to fit the cell' },
  { id: 'S',    label: 'S',    title: 'Small - locked font size' },
  { id: 'M',    label: 'M',    title: 'Medium - locked font size' },
  { id: 'L',    label: 'L',    title: 'Large - locked font size' },
  { id: 'XL',   label: 'XL',   title: 'Extra large - locked font size' },
];

interface GridCellRowsProps {
  /** Accessor, not raw value — must return the live ButtonGridItem so
   *  cell edits flow through. If a stale reference is passed (e.g. from
   *  an IIFE captured at mount), the <Index> iterates a frozen cells
   *  array and inputs desync from the store. */
  grid: Accessor<ButtonGridItem>;
}

export function GridCellRows(props: GridCellRowsProps) {
  // Snapshot the grid id ONCE at mount — the accessor updates as
  // editGridCell fires but the id stays stable, and we want a stable
  // key for the scroll-preservation map even if props.grid() churns.
  const gridId = props.grid().id;
  return (
    <div
      class="bg-cell-rows"
      data-grid-id={gridId}
      aria-label="Edit grid cells"
      ref={(el) => {
        // Restore scroll position after a store-triggered remount.
        // Defer to next frame so the browser has finished laying out
        // the rebuilt children — otherwise scrollHeight is still 0.
        const saved = rowsScrollByGrid.get(gridId);
        if (saved !== undefined) {
          requestAnimationFrame(() => { el.scrollTop = saved; });
        }
      }}
      onScroll={(e) => {
        rowsScrollByGrid.set(gridId, e.currentTarget.scrollTop);
      }}
    >
      {/* <Index> keys rows by position — cell identity churn (from
          editGridCell spreading) doesn't remount, so the focused
          Label input keeps focus across keystrokes. The <For> variant
          remounts on every letter typed. */}
      <Index each={props.grid().cells}>
        {(cell, index) => <GridCellRow grid={props.grid} cell={cell} index={index} />}
      </Index>
    </div>
  );
}

interface GridCellRowProps {
  grid: Accessor<ButtonGridItem>;
  cell: Accessor<ButtonGridCell>;
  index: number;
}

/** Resize a textarea to fit its content (no scrollbar, no min-1-line
 *  jitter). Reset height first so shrinks work when content deletes.
 *  Called on input + after each store push (via createMemo tracking
 *  props.cell().label). Runs on the current element; safe to call with
 *  undefined. */
function autoResize(el: HTMLTextAreaElement | undefined) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

function GridCellRow(props: GridCellRowProps) {
  let rootEl: HTMLDivElement | undefined;
  let labelInputEl: HTMLTextAreaElement | undefined;

  // Snapshot cell id at mount — the focus effect below then depends
  // only on the focus signal, not on cell reactivity.
  const cellId = props.cell().id;

  // Uncontrolled inputs: local mirrors of label / cc / ch that only
  // sync to the store on blur / Enter, not on every keystroke. Every
  // keystroke pushed to the store causes Solid's <Show> block above
  // this component to remount (item ref churn → store update → some
  // reactive edge in Solid 1.9 unmounts + re-mounts the buttongrid
  // Show subtree), which was tearing focus out of the input after
  // the first letter. Committing on blur means one store write per
  // completed edit, which the remount can't hurt because focus has
  // already left. Reads still come from the reactive cell() so if
  // the store updates from elsewhere (e.g. another edit) the row
  // reflects it — as long as this input isn't currently focused.
  const [labelDraft, setLabelDraft] = createSignal(props.cell().label ?? '');
  const [ccDraft, setCcDraft] = createSignal<string>(
    String(props.cell().ccNumber ?? ((props.grid().baseCC + props.index) & 0x7f)),
  );
  const [chDraft, setChDraft] = createSignal<string>(
    String((props.cell().channel ?? props.grid().channel) + 1),
  );

  const focus = focusCellRow();
  createMemo(() => {
    const req = focus();
    if (!req || req.cellId !== cellId) return;
    queueMicrotask(() => {
      rootEl?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      labelInputEl?.focus();
      labelInputEl?.select();
    });
  });

  function patch(p: Partial<Omit<ButtonGridCell, 'id'>>) {
    editGridCell(props.grid().id, props.cell().id, p);
  }
  // Undefined while the cell takes the grid's colour: no swatch is lit.
  const currentColor = () => props.cell().color ?? (hueOf(props.grid()) === 'none' ? 'cyan' : undefined);

  // ── Matrix picker for THIS cell (2026-07-31, DEC-143) ──────────────
  // A grid cell binds its own (cc, channel) - overriding the grid's
  // `baseCC + index` derivation - so it needs its own picker rather than
  // the item-level one in InspectorPanel, which writes to the ITEM.
  const [matrixOpen, setMatrixOpen] = createSignal(false);
  /** The cell's effective binding: its override if set, else the grid's
   *  row-major derivation. A grid cell therefore ALWAYS has a real
   *  current cell (unlike a fresh fader sitting at the 0/0 default), so
   *  the picker's highlight is always meaningful here. */
  const effectiveCc = () => props.cell().ccNumber ?? ((props.grid().baseCC + props.index) & 0x7f);
  const effectiveCh = () => props.cell().channel ?? props.grid().channel;
  /** Cells are seeded with their index as a label ("3"), so the raw label
   *  alone reads as "Assigning 3" - true and useless. Always carry the
   *  grid's identity, and say "cell" when the label is just the number. */
  const matrixLabel = () => {
    const raw = props.cell().label?.trim() ?? '';
    const isJustIndex = raw === '' || raw === String(props.index + 1);
    const cell = isJustIndex ? `cell ${props.index + 1}` : raw;
    return `${props.grid().name?.trim() || 'Grid'} · ${cell}`;
  };
  function onMatrixPick(ch: number, cc: number): void {
    patch({ ccNumber: cc, channel: ch });
    // The cc/ch inputs are uncontrolled drafts seeded once at mount (see
    // above), so a store write alone would leave them showing the OLD
    // numbers next to a cell that had actually moved.
    setCcDraft(String(cc));
    setChDraft(String(ch + 1));
  }

  /** Tab walks down one column of the cell list (operator, 2026-10-15:
   *  "if i'm editting the name, i want tab to goto the next cell name, if
   *  i'm in cc number, tab should goto next cc number. same with channel"),
   *  Shift+Tab walks up, both wrapping at the ends. The field commits first;
   *  the target is looked up after the write lands, because a commit can
   *  rebuild the rows and a reference taken before it would be detached. */
  function tabTo(e: KeyboardEvent, field: 'label' | 'cc' | 'ch', commit: () => void): void {
    if (e.key !== 'Tab' || e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    const gridId = props.grid().id;
    const count = props.grid().cells.length;
    const next = (props.index + (e.shiftKey ? count - 1 : 1)) % count;
    commit();
    requestAnimationFrame(() => {
      const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
        `.bg-cell-rows[data-grid-id="${CSS.escape(gridId)}"] [data-cell-field="${field}"]`);
      const target = fields[next];
      if (!target) return;
      target.focus();
      target.select();
      target.closest('.bg-cell-row')?.scrollIntoView({ block: 'nearest' });
    });
  }

  function commitLabel() {
    const v = labelDraft();
    if (v === (props.cell().label ?? '')) return; // no-op
    patch({ label: v });
  }
  function commitCC() {
    const n = Math.max(0, Math.min(127, parseInt(ccDraft(), 10) || 0));
    setCcDraft(String(n));
    const current = props.cell().ccNumber ?? ((props.grid().baseCC + props.index) & 0x7f);
    if (n === current) return;
    patch({ ccNumber: n });
  }
  function commitCh() {
    const n = Math.max(1, Math.min(16, parseInt(chDraft(), 10) || 1)) - 1; // store is 0-15
    setChDraft(String(n + 1));
    const current = props.cell().channel ?? props.grid().channel;
    if (n === current) return;
    patch({ channel: n });
  }

  return (
    <div class="bg-cell-row" data-parity={props.index % 2 === 0 ? 'odd' : 'even'} ref={(el) => (rootEl = el)}>
      {/* Left gutter — index number, centered against the full row
          height (spans all three lines). */}
      <span class="bg-cell-row-index">{props.index + 1}</span>
      {/* Right column — three stacked lines. */}
      <div class="bg-cell-row-body">
        {/* Line 1 — title. Textarea so labels can span multiple lines
            (Shift+Enter or Ctrl+Enter inserts a newline; plain Enter
            commits + blurs to match the numeric inputs). Rows=1 with
            auto-grow via CSS field-sizing so short labels don't take
            more space than they need. */}
        <div class="bg-cell-row-line bg-cell-row-line-title">
          <textarea
            class="bg-cell-row-label"
            placeholder={`Cell ${props.index + 1}`}
            rows={1}
            value={labelDraft()}
            onInput={(e) => {
              setLabelDraft(e.currentTarget.value);
              autoResize(e.currentTarget);
            }}
            data-cell-field="label"
            onBlur={commitLabel}
            onKeyDown={(e) => {
              tabTo(e, 'label', commitLabel);
              if (e.key !== 'Enter') return;
              if (e.shiftKey || e.ctrlKey || e.metaKey) return; // let newline through
              e.preventDefault();
              commitLabel();
              (e.currentTarget as HTMLTextAreaElement).blur();
            }}
            ref={(el) => { labelInputEl = el; queueMicrotask(() => autoResize(el)); }}
          />
        </div>
        {/* Line 2 — MIDI info (cc + ch) */}
        <div class="bg-cell-row-line bg-cell-row-line-midi">
          <label class="bg-cell-row-num">
            <span>cc</span>
            <input
              type="number" min="0" max="127"
              value={ccDraft()}
              onInput={(e) => setCcDraft(e.currentTarget.value)}
              data-cell-field="cc"
              onBlur={commitCC}
              onKeyDown={(e) => { tabTo(e, 'cc', commitCC); if (e.key === 'Enter') { commitCC(); (e.currentTarget as HTMLInputElement).blur(); } }}
            />
          </label>
          <label class="bg-cell-row-num">
            <span>ch</span>
            <input
              type="number" min="1" max="16"
              value={chDraft()}
              onInput={(e) => setChDraft(e.currentTarget.value)}
              data-cell-field="ch"
              onBlur={commitCh}
              onKeyDown={(e) => { tabTo(e, 'ch', commitCh); if (e.key === 'Enter') { commitCh(); (e.currentTarget as HTMLInputElement).blur(); } }}
            />
          </label>
          <button
            type="button"
            class="bg-cell-row-matrix"
            onClick={() => setMatrixOpen(true)}
            title="Pick this cell's CC + channel from the rig-wide MIDI matrix"
          >
            Matrix…
          </button>
        </div>
        {/* Line 3 — color strip (all swatches on one line) */}
        {/* Line 3 — font-size preset */}
        <div class="bg-cell-row-line bg-cell-row-font">
          <span class="bg-cell-row-font-label">font</span>
          <For each={NAME_SIZES}>
            {(s) => (
              <button
                type="button"
                class="bg-cell-row-font-btn"
                data-selected={(props.cell().nameSize ?? props.grid().nameSize ?? 'auto') === s.id ? 'true' : 'false'}
                onClick={() => patch({ nameSize: s.id })}
                title={s.title}
              >
                {s.label}
              </button>
            )}
          </For>
        </div>
        {/* Line 4 — color strip (all swatches on one line) */}
        <div class="bg-cell-row-line bg-cell-row-colors">
          <For each={ALL_COLORS}>
            {(c) => (
              <button
                type="button"
                class="bg-color-swatch"
                data-color={CELL_COLOR_TOKEN[c]}
                data-selected={currentColor() === c ? 'true' : 'false'}
                onClick={() => patch({ color: c })}
                aria-label={c}
                title={c}
              />
            )}
          </For>
        </div>
      </div>
      {/* Per-cell matrix picker. Rendered here rather than hoisted because
          only one row's picker is ever open, and keeping it local means the
          pick lands on THIS cell with no id plumbing. */}
      <Show when={matrixOpen()}>
        <MatrixPicker
          currentChannel={effectiveCh()}
          currentCcNumber={effectiveCc()}
          forLabel={matrixLabel()}
          onPick={onMatrixPick}
          onClose={() => setMatrixOpen(false)}
        />
      </Show>
    </div>
  );
}
